import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useState } from 'react';
import { Box, Typography, Button, MenuItem, Select, Stack, Modal, TextField, IconButton, InputLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetSettingsQuery, useUpdateSepBannerMutation, useDeleteSepBannerMutation, useInsertSepBannerMutation } from '../../../services/appSlice/appSlice';
import { Setting } from '../../../../shared/types/Settings';

const textFieldSx = {
  // color: '#222222',
  '& > p': {
    margin: '5px 0px',
    color: '#646669',
    fontSize: typographyTokens.fontSizes.xs,
  },
};

const colorMap: Record<string, string> = {
  red: '#ffcccc',
  yellow: '#FFD700',
  green: '#c8e6c9',
};

const SEPBanner: React.FC = () => {
  const { data: settings } = useGetSettingsQuery();
  const [updateSepBanner] = useUpdateSepBannerMutation();
  const [deleteSepBanner] = useDeleteSepBannerMutation();
  const [insertSepBanner] = useInsertSepBannerMutation();

  // Modal state for adding/editing banner
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newColor, setNewColor] = useState('yellow');
  const [newMessage, setNewMessage] = useState('');
  const [editBanner, setEditBanner] = useState<Setting | null>(null);

  // Filter for SEP Banner section
  const banners = (settings || []).filter((s: Setting) => s.section === 'SEP Banner').sort((a, b) => a.order - b.order);

  // Function to render message with blue colored link text (non-clickable for admin table)
  const renderMessageWithBlueLinks = (message: string) => {
    const cleanMessage = message.replace(' (active)', '');
    const elements: (JSX.Element | string)[] = [];
    let lastIndex = 0;
    
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
    const plainUrlRegex = /https?:\/\/[^\s]+/g;
    
    const markdownMatches: Array<{ start: number; end: number; text: string }> = [];
    let match;
    
    while ((match = markdownLinkRegex.exec(cleanMessage)) !== null) {
      markdownMatches.push({ start: match.index, end: match.index + match[0].length, text: match[1] });
    }
    
    for (let i = 0; i < markdownMatches.length; i++) {
      const mdMatch = markdownMatches[i];
      if (mdMatch.start > lastIndex) {
        const segment = cleanMessage.substring(lastIndex, mdMatch.start);
        elements.push(...processPlainText(segment, elements.length));
      }
      elements.push(<span key={elements.length} style={{ color: '#0066cc', fontWeight: typographyTokens.fontWeights.semibold }}>{mdMatch.text}</span>);
      lastIndex = mdMatch.end;
    }
    
    if (lastIndex < cleanMessage.length) {
      elements.push(...processPlainText(cleanMessage.substring(lastIndex), elements.length));
    }
    
    return elements.length > 0 ? elements : cleanMessage;
    
    function processPlainText(text: string, startKey: number): (JSX.Element | string)[] {
      const parts = text.split(plainUrlRegex);
      const urls = text.match(plainUrlRegex) || [];
      const result: (JSX.Element | string)[] = [];
      parts.forEach((part, i) => {
        if (part) result.push(part);
        if (urls[i]) result.push(<span key={`${startKey}-${i}`} style={{ color: '#0066cc', fontWeight: typographyTokens.fontWeights.semibold }}>{urls[i]}</span>);
      });
      return result;
    }
  };

  // Helper function to handle paste events and convert HTML links to markdown
  const handlePasteWithLinks = (e: React.ClipboardEvent<HTMLDivElement>, currentValue: string, setValue: (val: string) => void) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const target = e.target as HTMLTextAreaElement;
    const start = target.selectionStart || 0;
    const end = target.selectionEnd || 0;
    
    let pastedContent = text;
    
    if (html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const processNode = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
        if (node.nodeName === 'A') {
          const link = node as HTMLAnchorElement;
          return `[${link.textContent || ''}](${link.href})`;
        }
        let result = '';
        node.childNodes.forEach(child => { result += processNode(child); });
        if (['BR', 'P', 'DIV'].includes(node.nodeName)) result += ' ';
        return result;
      };
      
      pastedContent = processNode(doc.body).trim().replace(/\s+/g, ' ');
    }
    
    const newValue = currentValue.substring(0, start) + pastedContent + currentValue.substring(end);
    setValue(newValue);
  };

  // Toggle active state (store as 'active' in value, e.g. 'Message (active)')
  const handleToggleActive = (banner: Setting) => {
    const isActive = banner.value.endsWith(' (active)');
    updateSepBanner({
      id: banner.id,
      body: {
        ...banner,
        value: isActive ? banner.value.replace(' (active)', '') : banner.value + ' (active)'
      },
    });
  };

  const handleDelete = async (banner: Setting) => {
    await deleteSepBanner(banner.id);
    // After deletion, re-fetch banners and reassign order
    setTimeout(async () => {
      const updatedSettings = settings || [];
      const bannersAfterDelete = updatedSettings.filter((s: Setting) => s.section === 'SEP Banner' && s.id !== banner.id)
        .sort((a: Setting, b: Setting) => a.order - b.order);
      for (let i = 0; i < bannersAfterDelete.length; i++) {
        await updateSepBanner({ id: bannersAfterDelete[i].id, body: { ...bannersAfterDelete[i], order: i }, suppressSnackbar: true });
      }
    }, 500);
  };

  // Edit button handler
  const handleEdit = (banner: Setting) => {
    setEditBanner(banner);
    // Extract color from name (handles suffixes like 'sepbanner highlighted with red #1')
    const colorMatch = banner.name.match(/(yellow|red|green)/i);
    setNewColor(colorMatch ? colorMatch[1].toLowerCase() : 'yellow');
    setNewMessage(banner.value.replace(' (active)', ''));
    setEditOpen(true);
  };

  return (
    <>
      <Box sx={{ position: 'relative', minHeight: 60 }}>
        <Button
          variant="contained"
          sx={{ position: 'absolute', top: 0, right: 24, zIndex: 2, textTransform: 'none' }}
          onClick={() => setAddOpen(true)}
        >
          Add Banner
        </Button>
      </Box>
      {banners.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 3, mt: 5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Message</TableCell>
                <TableCell sx={{ minWidth: 40, width: 40 }}>Color</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {banners.map((banner: Setting) => {
                const isActive = banner.value.endsWith(' (active)');
                // Extract the last color from descriptive name (e.g., 'sepbanner highlighted with yellow' -> 'yellow')
                // Extract color from descriptive name (e.g., 'sepbanner highlighted with red #1' -> 'red')
                let color = banner.name;
                const colorMatch = banner.name.match(/(yellow|red|green)/i);
                if (colorMatch) {
                  color = colorMatch[1].toLowerCase();
                }
                // For order dropdown
                const orderOptions = Array.from({ length: banners.length }, (_, i) => i);
                return (
                  <TableRow key={banner.id}>
                    <TableCell>{renderMessageWithBlueLinks(banner.value)}</TableCell>
                    <TableCell sx={{ minWidth: 40, width: 40 }}>
                      <Box sx={{ background: colorMap[color] || '#fff', borderRadius: 1, p: 1, minWidth: 40, textAlign: 'center' }}>
                        {color}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={isActive}
                        onChange={() => handleToggleActive(banner)}
                        color="primary"
                        inputProps={{ 'aria-label': 'active toggle' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={banner.order}
                        onChange={async (e) => {
                          const newOrder = Number(e.target.value);
                          // Find the banner currently at newOrder and swap
                          const otherBanner = banners.find(b => b.order === newOrder);
                          if (otherBanner && otherBanner.id !== banner.id) {
                            // Swap orders
                            await updateSepBanner({ id: otherBanner.id, body: { ...otherBanner, order: banner.order }, suppressSnackbar: true });
                          }
                          await updateSepBanner({ id: banner.id, body: { ...banner, order: newOrder }, suppressSnackbar: true });
                        }}
                        sx={{ minWidth: 60 }}
                      >
                        {orderOptions.map((order) => (
                          <MenuItem key={order} value={order}>{order + 1}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(banner)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(banner)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {banners.length === 0 && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <Typography>No banners added yet.</Typography>
        </Box>
      )}
      {/* Removed duplicate Add Banner button below the table */}
      {/* Add Banner Modal */}
      {addOpen && (
        <Modal open={addOpen} onClose={() => setAddOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
          }}>
            <Box sx={{ borderBottom: '1px solid #e0e0e0', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>Add SEP Banner</Typography>
              <IconButton onClick={() => setAddOpen(false)} size="small"><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ marginBottom: '24px' }}>
                <InputLabel htmlFor="banner-message">Banner Message</InputLabel>
                <TextField
                  sx={textFieldSx}
                  size="small"
                  id="banner-message"
                  name="banner-message"
                  fullWidth
                  value={newMessage}
                  placeholder="Enter banner message..."

                  multiline
                  minRows={2}
                  onChange={e => setNewMessage(e.target.value)}
                  onPaste={(e) => handlePasteWithLinks(e, newMessage, setNewMessage)}
                />
              </Box>
              <Box>
                <InputLabel  htmlFor="banner-color">Banner Color</InputLabel>
                <Select
                  sx={textFieldSx}
                  size="small"
                  id="banner-color"
                  name="banner-color"
                  fullWidth
                  value={newColor}
                  onChange={e => setNewColor(e.target.value as 'red' | 'yellow' | 'green')}
                >
                  <MenuItem value="red">Red</MenuItem>
                  <MenuItem value="yellow">Yellow</MenuItem>
                  <MenuItem value="green">Green</MenuItem>
                </Select>
              </Box>
              <Stack direction="row" display="flex" justifyContent="right" spacing={2} sx={{ pb: 1, pt: 3 }}>
                <Button
                  type="submit"
                  sx={{ backgroundColor: '#2372B9', textTransform: 'none' }}
                  variant="contained"
                  disabled={!newMessage.trim()}
                  onClick={async () => {
                    // Find max order for unique order assignment
                    const maxOrder = banners.length > 0 ? Math.max(...banners.map(b => b.order)) + 1 : 0;
                    // Always assign unique color+suffix for new banners
                    const sameColorBanners = banners.filter(b => {
                      const match = b.name.match(/sepbanner highlighted with (yellow|red|green)(?: #(\d+))?/i);
                      return match && match[1].toLowerCase() === newColor;
                    });
                    let suffix = '';
                    if (sameColorBanners.length > 0) {
                      // Find all used suffix numbers
                      const usedNumbers = sameColorBanners.map(b => {
                        const m = b.name.match(/ #(\d+)$/);
                        return m ? parseInt(m[1], 10) : 1;
                      });
                      let nextNum = 1;
                      while (usedNumbers.includes(nextNum)) nextNum++;
                      suffix = ` #${nextNum}`;
                    }
                    const descriptiveName = `sepbanner highlighted with ${newColor}${suffix}`;
                    await insertSepBanner({
                      name: descriptiveName,
                      value: newMessage,
                      section: 'SEP Banner',
                      order: maxOrder,
                    });
                    setAddOpen(false);
                    setNewMessage('');
                    setNewColor('yellow');
                  }}
                >
                  Save Banner
                </Button>
              </Stack>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Edit Banner Modal */}
      {editOpen && editBanner && (
        <Modal open={editOpen} onClose={() => setEditOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
          }}>
            <Box sx={{ borderBottom: '1px solid #e0e0e0', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>Edit SEP Banner</Typography>
              <IconButton onClick={() => setEditOpen(false)} size="small"><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ marginBottom: '24px' }}>
                <InputLabel htmlFor="edit-banner-message">Banner Message</InputLabel>
                <TextField
                  sx={textFieldSx}
                  size="small"
                  id="edit-banner-message"
                  name="edit-banner-message"
                  fullWidth
                  value={newMessage}
                  placeholder="Enter banner message..."
                  helperText="This message will appear on the SEP Header."
                  multiline
                  minRows={2}
                  onChange={e => setNewMessage(e.target.value)}
                  onPaste={(e) => handlePasteWithLinks(e, newMessage, setNewMessage)}
                />
              </Box>
              <Box>
                <InputLabel htmlFor="edit-banner-color">Banner Color</InputLabel>
                <Select
                  sx={textFieldSx}
                  size="small"
                  id="edit-banner-color"
                  name="edit-banner-color"
                  fullWidth
                  value={newColor}
                  onChange={e => setNewColor(e.target.value as 'red' | 'yellow' | 'green')}
                >
                  <MenuItem value="red">Red</MenuItem>
                  <MenuItem value="yellow">Yellow</MenuItem>
                  <MenuItem value="green">Green</MenuItem>
                </Select>
              </Box>
              <Stack direction="row" display="flex" justifyContent="right" spacing={2} sx={{ pb: 1, pt: 3 }}>
                <Button
                  type="submit"
                  sx={{ backgroundColor: '#2372B9', textTransform: 'none' }}
                  variant="contained"
                  disabled={!newMessage.trim()}
                  onClick={async () => {
                    // Check if color changed
                    const oldColorMatch = editBanner.name.match(/(yellow|red|green)/i);
                    const oldColor = oldColorMatch ? oldColorMatch[1].toLowerCase() : '';
                    const colorChanged = oldColor !== newColor;

                    let descriptiveName = '';
                    if (colorChanged) {
                      // If color changed, assign a new unique suffix for the new color
                      const sameColorBanners = banners.filter(b => {
                        const match = b.name.match(/sepbanner highlighted with (yellow|red|green)(?: #(\d+))?/i);
                        return match && match[1].toLowerCase() === newColor && b.id !== editBanner.id;
                      });
                      let suffix = '';
                      if (sameColorBanners.length > 0) {
                        const usedNumbers = sameColorBanners.map(b => {
                          const m = b.name.match(/ #(\d+)$/);
                          return m ? parseInt(m[1], 10) : 1;
                        });
                        let nextNum = 1;
                        while (usedNumbers.includes(nextNum)) nextNum++;
                        suffix = ` #${nextNum}`;
                      }
                      descriptiveName = `sepbanner highlighted with ${newColor}${suffix}`;
                    } else {
                      // If color didn't change, keep the same suffix
                      let editSuffix = '';
                      const editMatch = editBanner.name.match(/(#[0-9]+)$/);
                      if (editMatch) {
                        editSuffix = ' ' + editMatch[1];
                      }
                      descriptiveName = `sepbanner highlighted with ${newColor}${editSuffix}`;
                    }

                    await updateSepBanner({
                      id: editBanner.id,
                      body: {
                        ...editBanner,
                        name: descriptiveName,
                        value: newMessage,
                      },
                    });
                    setEditOpen(false);
                    setEditBanner(null);
                    setNewMessage('');
                    setNewColor('yellow');
                  }}
                >
                  Save Changes
                </Button>
              </Stack>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default SEPBanner;